import { cloneDeep } from 'lodash';
import apiClient from '@/utils/apiClient';

const DRAFTS_ENDPOINT = '/api/price-list-drafts/';

/**
 * Lista în lucru is scoped by price-list *type* only (not a concrete list id).
 * ``price_list_id: 'none'`` keeps the request shape backward-compatible.
 *
 * @param {string} priceListType
 * @returns {{ type: string, price_list_id: string }}
 */
function draftScopeParams(priceListType) {
    return {
        type: priceListType || 'general',
        price_list_id: 'none',
    };
}

/**
 * Loads all product drafts for a price-list type (independent of selected list).
 * Extra args (legacy priceListId) are ignored.
 * @param {string} priceListType
 * @returns {Promise<Array<Object>>}
 */
export async function fetchDraftQueue(priceListType) {
    try {
        const response = await apiClient.get(DRAFTS_ENDPOINT, {
            params: draftScopeParams(priceListType),
        });
        return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
        console.error('Failed to load price list drafts:', error);
        return [];
    }
}

/**
 * @param {unknown} id
 * @returns {boolean}
 */
export function isPersistedDraftId(id) {
    if (id == null || id === '') {
        return false;
    }
    if (typeof id === 'number') {
        return Number.isInteger(id) && id > 0;
    }
    const normalized = String(id).trim();
    return /^\d+$/.test(normalized);
}

/**
 * @param {unknown} id
 * @returns {number|null}
 */
export function normalizePersistedDraftId(id) {
    if (!isPersistedDraftId(id)) {
        return null;
    }
    return typeof id === 'number' ? id : Number(String(id).trim());
}

/**
 * @param {Object} entry
 * @param {string} priceListType
 * @returns {Object}
 */
function buildDraftRequestBody(entry, priceListType) {
    const body = {
        ...draftScopeParams(priceListType),
        action: entry.action,
        updateMode: entry.updateMode,
        payload: entry.payload,
        builderState: entry.builderState,
        summary: entry.summary,
        sourceProductCode: entry.sourceProductCode,
        previousPrice: entry.previousPrice,
    };
    const persistedId = normalizePersistedDraftId(entry?.id);
    if (persistedId != null) {
        body.id = persistedId;
    }
    return body;
}

/**
 * Creates or updates a single draft entry on the backend.
 * @param {Object} params
 * @param {string} params.priceListType
 * @param {Object} params.entry - Draft entry (see createDraftEntry).
 * @returns {Promise<Object>} The persisted draft entry (with server id).
 */
export async function saveDraftEntry({ priceListType, entry }) {
    const body = buildDraftRequestBody(entry, priceListType);
    const response = await apiClient.post(DRAFTS_ENDPOINT, body);
    return response.data;
}

const BULK_DRAFTS_ENDPOINT = `${DRAFTS_ENDPOINT}bulk/`;
const PUBLISH_DRAFTS_ENDPOINT = `${DRAFTS_ENDPOINT}publish/`;
const DEFAULT_BULK_CHUNK_SIZE = 60;
const DEFAULT_PUBLISH_CHUNK_SIZE = 20;

/**
 * Upserts many drafts in chunked bulk requests (avoids one-by-one HTTP latency).
 * @param {Object} params
 * @param {string} params.priceListType
 * @param {Array<Object>} params.entries
 * @param {number} [params.chunkSize]
 * @param {(saved: number, total: number) => void} [params.onProgress]
 * @returns {Promise<{ saved: Object[], failedCodes: string[] }>}
 */
export async function saveDraftEntriesBulk({
    priceListType,
    entries,
    chunkSize = DEFAULT_BULK_CHUNK_SIZE,
    onProgress,
}) {
    const list = Array.isArray(entries) ? entries : [];
    if (!list.length) {
        return { saved: [], failedCodes: [] };
    }
    const size = Math.max(1, Math.min(80, Number(chunkSize) || DEFAULT_BULK_CHUNK_SIZE));
    const saved = [];
    const failedCodes = [];

    for (let offset = 0; offset < list.length; offset += size) {
        const chunk = list.slice(offset, offset + size);
        const drafts = chunk.map((entry) =>
            buildDraftRequestBody(entry, priceListType),
        );
        try {
            const response = await apiClient.post(BULK_DRAFTS_ENDPOINT, {
                ...draftScopeParams(priceListType),
                drafts,
            });
            const batch = Array.isArray(response.data?.drafts)
                ? response.data.drafts
                : [];
            saved.push(...batch);
            const errors = Array.isArray(response.data?.errors)
                ? response.data.errors
                : [];
            errors.forEach((err) => {
                failedCodes.push(String(err?.product_code || 'necunoscut').trim() || 'necunoscut');
            });
        } catch (error) {
            console.error('Failed to save draft bulk chunk:', error);
            chunk.forEach((entry) => {
                failedCodes.push(
                    String(entry?.payload?.product_code || 'necunoscut').trim() || 'necunoscut',
                );
            });
        }
        if (typeof onProgress === 'function') {
            onProgress(Math.min(offset + chunk.length, list.length), list.length);
        }
    }

    return { saved, failedCodes };
}

/**
 * Normalizes one or more publish target price list ids.
 * Prefers ``priceListIds``; falls back to single ``priceListId``.
 * @param {Object} params
 * @param {Array<number|string>|null|undefined} [params.priceListIds]
 * @param {number|string|null|undefined} [params.priceListId]
 * @returns {Array<number|string>}
 */
export function normalizePublishPriceListIds({
    priceListIds = null,
    priceListId = null,
} = {}) {
    const raw = Array.isArray(priceListIds) && priceListIds.length
        ? priceListIds
        : (priceListId != null && priceListId !== '' ? [priceListId] : []);
    const unique = [];
    const seen = new Set();
    for (const rawId of raw) {
        const id = typeof rawId === 'object' && rawId != null
            ? rawId.id
            : rawId;
        if (id == null || id === '') {
            continue;
        }
        const key = String(id);
        if (seen.has(key)) {
            continue;
        }
        seen.add(key);
        unique.push(id);
    }
    return unique;
}

const DEFAULT_PUBLISH_CONCURRENCY = 3;

function isDeadlockPublishError(message) {
    return /deadlock detected/i.test(String(message || ''));
}

/**
 * Publishes many drafts into one or more target price lists via chunked bulk requests.
 * Chunks run with limited concurrency so large queues finish faster without
 * flooding the API/DB. Successful drafts are deleted server-side.
 *
 * Multi-list publish is forced to concurrency 1: parallel workers updating the
 * same PriceList rows cause PostgreSQL deadlocks (warehouse_pricelist).
 *
 * @param {Object} params
 * @param {number|string} [params.priceListId] - Single target (legacy)
 * @param {Array<number|string>} [params.priceListIds] - Multiple targets
 * @param {Array<number|string>} params.draftIds - Persisted draft ids
 * @param {number} [params.chunkSize]
 * @param {number} [params.concurrency] - Parallel chunk requests (1–4); ignored when multi-list
 * @param {(done: number, total: number) => void} [params.onProgress]
 * @returns {Promise<{
 *   succeeded: Array<{draft_id: number, product_code: string}>,
 *   failed: Array<{draft_id: number, product_code: string, error: string}>,
 *   priceListIds: Array<number|string>,
 * }>}
 */
export async function publishDraftEntriesBulk({
    priceListId,
    priceListIds,
    draftIds,
    chunkSize = DEFAULT_PUBLISH_CHUNK_SIZE,
    concurrency = DEFAULT_PUBLISH_CONCURRENCY,
    onProgress,
}) {
    const ids = (Array.isArray(draftIds) ? draftIds : [])
        .map((id) => normalizePersistedDraftId(id))
        .filter((id) => id != null);
    const targetListIds = normalizePublishPriceListIds({ priceListIds, priceListId });
    if (!ids.length || !targetListIds.length) {
        return { succeeded: [], failed: [], priceListIds: targetListIds };
    }
    const listCount = targetListIds.length;
    // Multi-list: sequential chunks only — concurrent workers deadlock on shared lists.
    const adaptiveDefault = listCount >= 2
        ? 20
        : DEFAULT_PUBLISH_CHUNK_SIZE;
    const adaptiveConcurrency = listCount >= 2
        ? 1
        : Math.min(3, Number(concurrency) || DEFAULT_PUBLISH_CONCURRENCY);
    const size = Math.max(
        1,
        Math.min(25, Number(chunkSize) || adaptiveDefault),
    );
    const workerCount = Math.max(
        1,
        Math.min(4, adaptiveConcurrency),
    );
    const chunks = [];
    for (let offset = 0; offset < ids.length; offset += size) {
        chunks.push(ids.slice(offset, offset + size));
    }

    const succeeded = [];
    const failed = [];
    let completedDrafts = 0;
    let nextChunkIndex = 0;

    const publishChunk = async (chunk) => {
        try {
            const response = await apiClient.post(PUBLISH_DRAFTS_ENDPOINT, {
                price_list_ids: targetListIds,
                // Legacy single-id field kept for older backends / logging.
                price_list_id: targetListIds[0],
                draft_ids: chunk,
            });
            const batchSucceeded = Array.isArray(response.data?.succeeded)
                ? response.data.succeeded
                : [];
            const batchFailed = Array.isArray(response.data?.failed)
                ? response.data.failed
                : [];
            succeeded.push(...batchSucceeded);
            failed.push(...batchFailed);
        } catch (error) {
            console.error('Failed to publish draft bulk chunk:', error);
            const message =
                error?.response?.data?.error
                || error?.response?.data?.detail
                || 'Cererea de publicare a eșuat.';
            chunk.forEach((draftId) => {
                failed.push({
                    draft_id: draftId,
                    product_code: '',
                    error: message,
                });
            });
        } finally {
            completedDrafts += chunk.length;
            if (typeof onProgress === 'function') {
                onProgress(Math.min(completedDrafts, ids.length), ids.length);
            }
        }
    };

    const workers = Array.from(
        { length: Math.min(workerCount, chunks.length) },
        async () => {
            while (nextChunkIndex < chunks.length) {
                const index = nextChunkIndex;
                nextChunkIndex += 1;
                // eslint-disable-next-line no-await-in-loop
                await publishChunk(chunks[index]);
            }
        },
    );
    await Promise.all(workers);

    // One sequential retry for drafts that lost a deadlock race (common on large queues).
    const deadlockIds = failed
        .filter((row) => isDeadlockPublishError(row?.error))
        .map((row) => normalizePersistedDraftId(row?.draft_id))
        .filter((id) => id != null);
    if (deadlockIds.length) {
        const remainingFailed = failed.filter(
            (row) => !isDeadlockPublishError(row?.error),
        );
        failed.length = 0;
        failed.push(...remainingFailed);
        for (let offset = 0; offset < deadlockIds.length; offset += size) {
            const chunk = deadlockIds.slice(offset, offset + size);
            try {
                // eslint-disable-next-line no-await-in-loop
                const response = await apiClient.post(PUBLISH_DRAFTS_ENDPOINT, {
                    price_list_ids: targetListIds,
                    price_list_id: targetListIds[0],
                    draft_ids: chunk,
                });
                const batchSucceeded = Array.isArray(response.data?.succeeded)
                    ? response.data.succeeded
                    : [];
                const batchFailed = Array.isArray(response.data?.failed)
                    ? response.data.failed
                    : [];
                succeeded.push(...batchSucceeded);
                failed.push(...batchFailed);
            } catch (error) {
                console.error('Failed to retry deadlock publish chunk:', error);
                const message =
                    error?.response?.data?.error
                    || error?.response?.data?.detail
                    || 'Cererea de publicare a eșuat.';
                chunk.forEach((draftId) => {
                    failed.push({
                        draft_id: draftId,
                        product_code: '',
                        error: message,
                    });
                });
            }
        }
    }

    return { succeeded, failed, priceListIds: targetListIds };
}

/**
 * Resolves the backend draft id for a queue entry (handles client UUID ids).
 * Extra args (legacy priceListId) are ignored.
 * @param {Object} draft
 * @param {string} priceListType
 * @returns {Promise<number|null>}
 */
export async function resolvePersistedDraftId(draft, priceListType) {
    const direct = normalizePersistedDraftId(draft?.id);
    if (direct != null) {
        return direct;
    }
    const productCode = String(draft.payload?.product_code || '').trim();
    if (!productCode) {
        return null;
    }
    const drafts = await fetchDraftQueue(priceListType);
    const match = drafts.find(
        (row) => String(row.payload?.product_code || '').trim() === productCode,
    );
    return normalizePersistedDraftId(match?.id);
}

/**
 * No-op migrate: lista în lucru is type-scoped, not per concrete list.
 * Kept so older call sites keep working.
 * @param {Object} params
 * @param {string} params.priceListType
 * @param {Object} params.entry
 * @returns {Promise<Object>}
 */
export async function migrateDraftEntry({
    priceListType,
    entry,
}) {
    return saveDraftEntry({
        priceListType,
        entry,
    });
}

/**
 * Deletes a single draft entry from the backend.
 * @param {number|string} draftId
 * @returns {Promise<void>}
 */
export async function deleteDraftEntry(draftId) {
    const persistedId = normalizePersistedDraftId(draftId);
    if (persistedId == null) {
        return;
    }
    await apiClient.delete(`${DRAFTS_ENDPOINT}${persistedId}/`);
}

const DEFAULT_DELETE_CHUNK_SIZE = 60;

/**
 * Deletes many drafts via chunked bulk DELETE (avoids N one-by-one HTTP calls).
 * @param {Object} params
 * @param {string} params.priceListType
 * @param {Array<number|string>} params.draftIds
 * @param {number} [params.chunkSize]
 * @param {(done: number, total: number) => void} [params.onProgress]
 * @returns {Promise<{ deleted: number, failedIds: Array<number|string> }>}
 */
export async function deleteDraftEntriesBulk({
    priceListType,
    draftIds,
    chunkSize = DEFAULT_DELETE_CHUNK_SIZE,
    onProgress,
}) {
    const ids = [...new Set(
        (Array.isArray(draftIds) ? draftIds : [])
            .map((id) => normalizePersistedDraftId(id))
            .filter((id) => id != null),
    )];
    if (!ids.length) {
        return { deleted: 0, failedIds: [] };
    }
    const size = Math.max(1, Math.min(80, Number(chunkSize) || DEFAULT_DELETE_CHUNK_SIZE));
    let deleted = 0;
    const failedIds = [];

    for (let offset = 0; offset < ids.length; offset += size) {
        const chunk = ids.slice(offset, offset + size);
        try {
            const response = await apiClient.delete(BULK_DRAFTS_ENDPOINT, {
                data: {
                    ...draftScopeParams(priceListType),
                    ids: chunk,
                },
            });
            deleted += Number(response.data?.deleted) || 0;
            const failed = Array.isArray(response.data?.failed)
                ? response.data.failed
                : [];
            failed.forEach((entry) => {
                if (entry?.id != null && entry.id !== '') {
                    failedIds.push(entry.id);
                }
            });
        } catch (error) {
            console.error('Failed to delete draft bulk chunk:', error);
            failedIds.push(...chunk);
        }
        if (typeof onProgress === 'function') {
            onProgress(Math.min(offset + chunk.length, ids.length), ids.length);
        }
    }

    return { deleted, failedIds };
}

/**
 * Clears all drafts for a price-list type on the backend.
 * Extra args (legacy priceListId) are ignored.
 * @param {string} priceListType
 * @returns {Promise<void>}
 */
export async function clearDraftQueue(priceListType) {
    await apiClient.delete(DRAFTS_ENDPOINT, {
        params: draftScopeParams(priceListType),
    });
}

/**
 * @returns {string}
 */
function createDraftId() {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        return crypto.randomUUID();
    }
    return `draft_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

/**
 * @param {Object} payload
 * @param {string[]} existingProductCodes
 * @returns {'create'|'update'}
 */
export function resolveDraftAction(payload, existingProductCodes = []) {
    if (payload?.old_product_code) {
        return 'update';
    }
    const code = String(payload?.product_code || '').trim();
    if (code && existingProductCodes.includes(code)) {
        return 'update';
    }
    return 'create';
}

/**
 * @param {Object} params
 * @param {'create'|'update'} params.action
 * @param {Object} params.payload
 * @param {Object} [params.builderState]
 * @param {string|null} [params.sourceProductCode]
 * @param {number|string|null} [params.previousPrice]
 * @param {'simple'|'builder'} [params.updateMode]
 * @returns {Object}
 */
export function createDraftEntry({
    action,
    payload,
    builderState = null,
    sourceProductCode = null,
    previousPrice = null,
    updateMode = builderState ? 'builder' : 'simple',
}) {
    const normalizedPayload = cloneDeep(payload);
    if (action === 'update' && !normalizedPayload.old_product_code) {
        normalizedPayload.old_product_code = sourceProductCode || normalizedPayload.product_code;
    }

    // Never attach pricing_detail on simple price-only updates.
    if (updateMode === 'simple') {
        delete normalizedPayload.pricing_detail;
        delete normalizedPayload.pricing_settings;
    }

    return {
        id: createDraftId(),
        action,
        updateMode,
        payload: normalizedPayload,
        builderState: builderState ? cloneDeep(builderState) : null,
        sourceProductCode: sourceProductCode || normalizedPayload.old_product_code || null,
        previousPrice,
        createdAt: new Date().toISOString(),
        summary: {
            product_code: normalizedPayload.product_code,
            product_name: normalizedPayload.product_name,
            price: normalizedPayload.price,
            previous_price: previousPrice,
            action,
            update_mode: updateMode,
        },
    };
}

/**
 * @param {Array<Object>} drafts
 * @param {Object} entry
 * @param {string|null} [replaceDraftId]
 * @returns {Array<Object>}
 */
export function upsertDraftEntry(drafts, entry, replaceDraftId = null) {
    const queue = Array.isArray(drafts) ? [...drafts] : [];
    if (replaceDraftId) {
        const index = queue.findIndex((draft) => draft.id === replaceDraftId);
        if (index !== -1) {
            queue[index] = { ...entry, id: replaceDraftId };
            return queue;
        }
    }
    queue.push(entry);
    return queue;
}

/**
 * @param {Array<Object>} drafts
 * @param {string} draftId
 * @returns {Array<Object>}
 */
export function removeDraftEntry(drafts, draftId) {
    return (drafts || []).filter((draft) => draft.id !== draftId);
}

/**
 * @param {Object|null|undefined} draft
 * @returns {boolean}
 */
export function isExchangeRateRecalcDraft(draft) {
    return Boolean(draft?.builderState?.exchangeRateRecalc);
}

/**
 * True when lista în lucru contains drafts from a list exchange-rate recalc.
 * Those FX drafts cannot be deleted one-by-one; other drafts still can.
 * @param {Array<Object>|null|undefined} drafts
 * @returns {boolean}
 */
export function draftsHaveExchangeRateRecalcLock(drafts) {
    return (Array.isArray(drafts) ? drafts : []).some(isExchangeRateRecalcDraft);
}

/**
 * @param {Array<Object>|null|undefined} drafts
 * @returns {Object[]}
 */
export function filterExchangeRateRecalcDrafts(drafts) {
    return (Array.isArray(drafts) ? drafts : []).filter(isExchangeRateRecalcDraft);
}

/**
 * @param {Array<Object>|null|undefined} drafts
 * @returns {Object[]}
 */
export function filterNonExchangeRateRecalcDrafts(drafts) {
    return (Array.isArray(drafts) ? drafts : []).filter(
        (draft) => !isExchangeRateRecalcDraft(draft),
    );
}

/**
 * Previous list FX rates stamped when exchange-rate recalc drafts were created.
 * @param {Array<Object>|null|undefined} drafts
 * @returns {{
 *   exchangeRateEur: unknown,
 *   exchangeRatePln: unknown,
 *   exchangeRateUsd: unknown,
 * }|null}
 */
export function resolveExchangeRateRecalcPrevious(drafts) {
    for (const draft of (Array.isArray(drafts) ? drafts : [])) {
        const prev = draft?.builderState?.exchangeRateRecalcPrevious;
        if (!prev || typeof prev !== 'object') {
            continue;
        }
        if (
            prev.exchangeRateEur == null
            && prev.exchangeRatePln == null
            && prev.exchangeRateUsd == null
        ) {
            continue;
        }
        return {
            exchangeRateEur: prev.exchangeRateEur,
            exchangeRatePln: prev.exchangeRatePln,
            exchangeRateUsd: prev.exchangeRateUsd,
        };
    }
    return null;
}

/**
 * Keep FX-recalc lock markers when the builder rewrites builderState on draft save.
 * @param {Object|null|undefined} nextBuilderState
 * @param {Object|null|undefined} previousBuilderState
 * @returns {Object|null|undefined}
 */
export function preserveExchangeRateRecalcOnBuilderState(
    nextBuilderState,
    previousBuilderState,
) {
    if (!nextBuilderState || !previousBuilderState?.exchangeRateRecalc) {
        return nextBuilderState;
    }
    return {
        ...nextBuilderState,
        exchangeRateRecalc: true,
        exchangeRateRecalcPrevious:
            previousBuilderState.exchangeRateRecalcPrevious || null,
    };
}

/**
 * FX-recalc drafts must be applied as a full wave (all or none).
 * Product-only selection (zero FX) is allowed; partial FX selection is not.
 * @param {{
 *   drafts?: Array<Object>|null,
 *   selectedIds?: Array<string|number>|null,
 * }} params
 * @returns {{
 *   ok: boolean,
 *   includesFullFxWave: boolean,
 *   selectedFxCount: number,
 *   totalFxCount: number,
 * }}
 */
export function resolveExchangeRatePublishSelection({
    drafts = null,
    selectedIds = null,
} = {}) {
    const allDrafts = Array.isArray(drafts) ? drafts : [];
    const fxDrafts = filterExchangeRateRecalcDrafts(allDrafts);
    const totalFxCount = fxDrafts.length;
    if (!totalFxCount) {
        return {
            ok: true,
            includesFullFxWave: false,
            selectedFxCount: 0,
            totalFxCount: 0,
        };
    }

    const selectedList = Array.isArray(selectedIds) ? selectedIds : [];
    const selectedSet = selectedList.length
        ? new Set(selectedList.map((id) => String(id)))
        : new Set(
            allDrafts
                .map((draft) => draft?.id)
                .filter((id) => id != null && id !== '')
                .map((id) => String(id)),
        );

    const selectedFxCount = fxDrafts.filter((draft) => (
        selectedSet.has(String(draft.id))
    )).length;

    if (selectedFxCount === 0) {
        return {
            ok: true,
            includesFullFxWave: false,
            selectedFxCount: 0,
            totalFxCount,
        };
    }
    if (selectedFxCount === totalFxCount) {
        return {
            ok: true,
            includesFullFxWave: true,
            selectedFxCount,
            totalFxCount,
        };
    }
    return {
        ok: false,
        includesFullFxWave: false,
        selectedFxCount,
        totalFxCount,
    };
}

/**
 * Whether Aplică should promote header FX into appliedExchangeRates.
 * Product-only publishes must not stamp pending curs as applied; commit only
 * when the full FX-recalc wave was published and none remain pending.
 * @param {{
 *   publishedDrafts?: Array<Object>|null,
 *   remainingDrafts?: Array<Object>|null,
 * }} params
 * @returns {boolean}
 */
export function shouldCommitAppliedListExchangeRates({
    publishedDrafts = null,
    remainingDrafts = null,
} = {}) {
    if (!draftsHaveExchangeRateRecalcLock(publishedDrafts)) {
        return false;
    }
    return !draftsHaveExchangeRateRecalcLock(remainingDrafts);
}

/**
 * @param {Array<Object>} drafts
 * @param {string|null} [excludeDraftId]
 * @returns {string[]}
 */
export function getDraftProductCodes(drafts, excludeDraftId = null) {
    return (drafts || [])
        .filter((draft) => draft.id !== excludeDraftId)
        .map((draft) => String(draft.payload?.product_code || '').trim())
        .filter(Boolean);
}

/**
 * @param {string} productCode
 * @param {Array<Object>} drafts
 * @param {string|null} [excludeDraftId]
 * @returns {Object|null}
 */
export function findDraftByProductCode(drafts, productCode, excludeDraftId = null) {
    const normalized = String(productCode || '').trim();
    if (!normalized) {
        return null;
    }
    return (drafts || []).find((draft) =>
        draft.id !== excludeDraftId
        && String(draft.payload?.product_code || '').trim() === normalized
    ) || null;
}
